import React, { useMemo, useState } from "react";
import auth from "services/auth";
import { __ } from "services/Translator";
import supportApi from "support/api/supportApi";
import FieldRenderer from "support/components/FieldRenderer";

// C-74: a signed-in player never fills in a contact form. The backend already
// knows who they are from the Bearer, so asking again is friction that also
// invites a mismatch between the typed details and the account.
function contactFields(fields) {
  const contactKeys = ["name", "full_name", "email", "email_address", "phone", "telephone"];
  return fields.filter((field) => contactKeys.indexOf(field.key) === -1);
}

export default function NewConversationView({ config, onCreated }) {
  const isAuthenticated = auth.isAuthenticated();
  const departments = (config && config.departments) || [];
  const forms = (config && config.forms) || {};

  const [departmentKey, setDepartmentKey] = useState(
    departments.length === 1 ? departments[0].key : ""
  );
  const [values, setValues] = useState({});
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // The form id follows the department; message is rendered by the composer
  // area below rather than as a field, so it is filtered out of the descriptor
  // list even when the backoffice includes it.
  const formFields = useMemo(() => {
    const raw = Array.isArray(forms[departmentKey]) ? forms[departmentKey] : [];
    const withoutMessage = raw.filter((field) => field.key !== "message");
    return isAuthenticated ? contactFields(withoutMessage) : withoutMessage;
  }, [forms, departmentKey, isAuthenticated]);

  const change = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const found = {};
    formFields.forEach((field) => {
      if (field.required && !values[field.key]) {
        found[field.key] = __("This field is required.");
      }
    });
    if (!message.trim()) {
      found.message = __("This field is required.");
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    setFormError(null);
    if (!validate() || submitting) {
      return;
    }
    setSubmitting(true);
    const subjectField = formFields.find((field) => field.key === "subject");
    const payload = {
      message: message.trim(),
      subject: subjectField ? values.subject : undefined,
      department_key: departmentKey || undefined,
      form_id: formFields.length ? departmentKey : undefined,
      fields: formFields.length
        ? formFields.map((field) => ({ key: field.key, value: values[field.key] ?? null }))
        : undefined,
    };
    const response = await supportApi.createConversation(payload);
    setSubmitting(false);

    if (response.status === 200 && response.data && response.data.conversation) {
      onCreated(response.data.conversation.reference);
      return;
    }
    if (response.status === 422 && response.data && response.data.validationErrors) {
      setErrors(response.data.validationErrors);
      return;
    }
    setFormError(__("We could not start this conversation. Please try again."));
  };

  return (
    <form className="supForm" onSubmit={submit} noValidate>
      {departments.length > 1 && (
        <div className="supField">
          <label className="supLabel" htmlFor="supDepartment">
            {__("What is this about?")}
          </label>
          <select
            id="supDepartment"
            className="supInput"
            value={departmentKey}
            onChange={(event) => setDepartmentKey(event.target.value)}
          >
            <option value="">{__("Choose a topic")}</option>
            {departments.map((department) => (
              <option key={department.key} value={department.key}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {formFields.map((field) => (
        <FieldRenderer
          key={field.key}
          field={field}
          value={values[field.key]}
          error={errors[field.key]}
          onChange={change}
        />
      ))}

      <div className="supField">
        <label className="supLabel" htmlFor="supNewMessage">
          {__("How can we help?")}
          <span className="supRequired" aria-hidden="true">
            *
          </span>
        </label>
        <textarea
          id="supNewMessage"
          className="supInput supTextarea"
          rows={5}
          value={message}
          required
          aria-invalid={errors.message ? "true" : undefined}
          aria-describedby={errors.message ? "supNewMessage-error" : undefined}
          onChange={(event) => {
            setMessage(event.target.value);
            if (errors.message) {
              setErrors((current) => {
                const next = { ...current };
                delete next.message;
                return next;
              });
            }
          }}
        />
        {errors.message && (
          <p className="supFieldError" id="supNewMessage-error">
            {errors.message}
          </p>
        )}
      </div>

      {formError && (
        <p className="supFieldError" role="status">
          {formError}
        </p>
      )}

      <button type="submit" className="supBtn supBtnPrimary supFormSubmit" disabled={submitting}>
        {submitting ? __("Sending") : __("Send")}
      </button>
    </form>
  );
}
